<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import { endpoints } from '@/api'
import { ApiError } from '@/api/client-exports'

/**
 * Creating a group (SPEC §6.3, §7.1).
 *
 * Two required fields, and nothing else on the first screen. SPEC §7.1 is
 * explicit that organizers must not be made to configure the product before
 * they see it work — so the optional details are behind a disclosure, and
 * skipping them costs nothing.
 */

const router = useRouter()

const name = ref('')
const timeZoneId = ref('')
const showOptional = ref(false)

const description = ref('')
const defaultLocationName = ref('')
const defaultStartLocalTime = ref('')

/*
  Who this group is for, and what follows from it (MT-061).

  Not a cosmetic question. A family already knows each other, so seeing who is
  bringing what by name is the point; a wider group of people who do not may
  prefer counts without names (SPEC §6.7). The choice is offered here because
  afterwards it lives on a settings screen nobody opens, and by then the first
  meal has already shown everybody's name to everybody.

  The family answer is the default and asks nothing further, because that is
  what this product is shaped around.
*/
type StartingPoint = 'family' | 'wider'

const startingPoint = ref<StartingPoint>('family')
const householdVisibility = ref<'Named' | 'Anonymous'>('Named')

/*
  Appendix A's eleven menus, offered rather than assumed.

  The alternative for a new organizer is an empty menu builder, which is the
  moment somebody decides a tool is not worth the trouble. Declining leaves the
  group genuinely empty -- these are seed data and nothing depends on them.
*/
const startWithCommonMeals = ref(true)

const submitting = ref(false)
const formError = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

const errorSummary = useTemplateRef<HTMLElement>('errorSummary')

/**
 * Zones the browser knows about, which is the platform's own list.
 *
 * Shipping a curated list would go stale the first time a zone is renamed or
 * split, and the server validates against its own database anyway — so a
 * mismatch here shows up as a rejected form rather than a wrong meal time.
 */
const zones = ref<string[]>([])

onMounted(() => {
  const supported = Intl.supportedValuesOf?.('timeZone') ?? []

  // The browser's own zone is right far more often than not, and an organizer
  // who lives where their family eats should not have to find it in a list.
  const guess = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Added to the list when it is not already there, which is not a rare edge:
  // supportedValuesOf returns canonical zone names and omits some a browser
  // will happily report, UTC among them. Selecting the default out of the list
  // meant a browser reporting one of those got no default and could not pick
  // its own zone at all -- caught by CI, which runs in UTC.
  zones.value = guess && !supported.includes(guess) ? [guess, ...supported] : supported

  if (guess) {
    timeZoneId.value = guess
  }
})

function errorFor(field: string): string | undefined {
  const key = Object.keys(fieldErrors.value).find((k) => k.toLowerCase() === field.toLowerCase())
  return key ? fieldErrors.value[key]?.[0] : undefined
}

async function submit(): Promise<void> {
  formError.value = null
  fieldErrors.value = {}

  const errors: Record<string, string[]> = {}
  if (!name.value.trim()) errors.name = ['Give the group a name.']
  if (!timeZoneId.value) errors.timeZoneId = ['Choose a time zone.']

  if (Object.keys(errors).length > 0) {
    fieldErrors.value = errors
    await focusError()
    return
  }

  submitting.value = true

  try {
    const group = await endpoints.createGroup({
      name: name.value.trim(),
      timeZoneId: timeZoneId.value,
      description: description.value.trim() || undefined,
      defaultLocationName: defaultLocationName.value.trim() || undefined,
      defaultStartLocalTime: defaultStartLocalTime.value || undefined,
      startWithCommonMeals: startWithCommonMeals.value,
      // Only when the group is not a family. Sending Named explicitly would
      // record a decision nobody made.
      householdVisibility: startingPoint.value === 'wider' ? householdVisibility.value : undefined,
    })

    await router.push({ name: 'organizer-dashboard', query: { created: group.id } })
  } catch (error) {
    if (error instanceof ApiError && error.problem?.errors) {
      fieldErrors.value = error.problem.errors
    } else {
      formError.value =
        error instanceof ApiError
          ? (error.problem?.detail ?? error.userMessage)
          : 'Something went wrong. Try again.'
    }

    await focusError()
  } finally {
    submitting.value = false
  }
}

async function focusError(): Promise<void> {
  await new Promise((resolve) => requestAnimationFrame(resolve))
  errorSummary.value?.focus()
}
</script>

<template>
  <v-container class="py-10" style="max-width: 34rem">
    <h1 class="text-h5 mb-2">Create your group</h1>
    <p class="text-body-2 mb-6" style="opacity: 0.75">
      A name and a time zone is all we need. Everything else can wait.
    </p>

    <div
      v-if="formError || Object.keys(fieldErrors).length > 0"
      ref="errorSummary"
      data-testid="create-group-error"
      role="alert"
      tabindex="-1"
      class="mb-4 pa-3 rounded text-body-2 outline-none"
      style="background-color: rgb(var(--v-theme-error)); color: rgb(var(--v-theme-on-error))"
    >
      {{ formError ?? 'Check the highlighted fields and try again.' }}
    </div>

    <v-form novalidate @submit.prevent="submit">
      <v-text-field
        v-model="name"
        label="Group name"
        name="name"
        placeholder="Sunday Family Lunch"
        :error-messages="errorFor('name')"
        :disabled="submitting"
        class="mb-2"
      />

      <v-autocomplete
        v-model="timeZoneId"
        :items="zones"
        label="Time zone"
        name="timeZoneId"
        :error-messages="errorFor('timeZoneId')"
        :disabled="submitting"
        class="mb-4"
      />

      <fieldset class="create__group-kind">
        <legend class="text-subtitle-2 mb-1">Who eats together?</legend>

        <v-radio-group
          v-model="startingPoint"
          :disabled="submitting"
          density="compact"
          hide-details
          data-testid="starting-point"
        >
          <v-radio label="A family" value="family" />
          <v-radio label="A wider group" value="wider" />
        </v-radio-group>

        <!--
          Only for the wider group. A family seeing each other's names is the
          product working as intended, so asking about it would be a question
          with an obvious answer and a cost in attention.
        -->
        <v-radio-group
          v-if="startingPoint === 'wider'"
          v-model="householdVisibility"
          :disabled="submitting"
          density="compact"
          hide-details
          class="mt-2"
          data-testid="household-visibility"
        >
          <template #label>
            <span class="text-body-2">What can people see about each other?</span>
          </template>
          <v-radio label="Names, and what each is bringing" value="Named" />
          <v-radio label="Numbers only, no names" value="Anonymous" />
        </v-radio-group>
      </fieldset>

      <v-checkbox
        v-model="startWithCommonMeals"
        :disabled="submitting"
        density="compact"
        hide-details
        class="mb-4"
        data-testid="start-with-common-meals"
      >
        <template #label>
          <span>
            Start with 11 common meals
            <span class="d-block text-caption" style="opacity: 0.75">
              Taco bar, soup lunch, lasagna and eight more. Edit or delete any of them.
            </span>
          </span>
        </template>
      </v-checkbox>

      <v-btn
        variant="text"
        size="small"
        class="mb-4 px-0"
        :aria-expanded="showOptional"
        aria-controls="optional-details"
        @click="showOptional = !showOptional"
      >
        {{ showOptional ? 'Hide' : 'Add' }} optional details
      </v-btn>

      <div v-show="showOptional" id="optional-details">
        <v-text-field
          v-model="description"
          label="Description"
          name="description"
          :disabled="submitting"
          class="mb-2"
        />

        <v-text-field
          v-model="defaultLocationName"
          label="Usual place"
          name="defaultLocationName"
          placeholder="Mabel's house"
          :disabled="submitting"
          class="mb-2"
        />

        <v-text-field
          v-model="defaultStartLocalTime"
          label="Usual start time"
          name="defaultStartLocalTime"
          type="time"
          :error-messages="errorFor('defaultStartLocalTime')"
          :disabled="submitting"
          class="mb-2"
        />
      </div>

      <v-btn
        type="submit"
        color="primary"
        size="large"
        block
        class="mt-4"
        :loading="submitting"
        :disabled="submitting"
      >
        Create group
      </v-btn>
    </v-form>
  </v-container>
</template>

<style scoped>
.outline-none:focus {
  outline: none;
}
</style>
